/**
 * Circuit Breaker Pattern Implementation
 * Prevents cascading failures by temporarily stopping requests when a service is failing
 * Now includes Sentry monitoring for production visibility
 */

import * as Sentry from '@sentry/react';

export enum CircuitState {
  CLOSED = 'CLOSED',     // Normal operation
  OPEN = 'OPEN',         // Failing, blocking requests
  HALF_OPEN = 'HALF_OPEN' // Testing if service recovered
}

export interface CircuitBreakerConfig {
  failureThreshold: number;    // Number of failures before opening circuit
  recoveryTimeout: number;     // Time to wait before trying again (ms)
  monitoringPeriod: number;    // Time window for counting failures (ms)
  halfOpenMaxCalls: number;    // Max calls allowed in half-open state
}

export class CircuitBreaker {
  private state: CircuitState = CircuitState.CLOSED;
  private failureCount: number = 0;
  private lastFailureTime: number = 0;
  private nextAttemptTime: number = 0;
  private halfOpenCalls: number = 0;
  private config: CircuitBreakerConfig;

  constructor(config: Partial<CircuitBreakerConfig> = {}) {
    this.config = {
      failureThreshold: 5,
      recoveryTimeout: 60000, // 1 minute
      monitoringPeriod: 60000, // 1 minute
      halfOpenMaxCalls: 3,
      ...config
    };
  }

  /**
   * Executes a function with circuit breaker protection
   */
  async execute<T>(operation: () => Promise<T>): Promise<T> {
    if (this.state === CircuitState.OPEN) {
      if (Date.now() < this.nextAttemptTime) {
        throw new Error('Circuit breaker is OPEN - service temporarily unavailable');
      }
      this.state = CircuitState.HALF_OPEN;
      this.halfOpenCalls = 0;
    }

    if (this.state === CircuitState.HALF_OPEN) {
      if (this.halfOpenCalls >= this.config.halfOpenMaxCalls) {
        throw new Error('Circuit breaker is HALF_OPEN - too many test calls');
      }
      this.halfOpenCalls++;
    }

    try {
      const result = await operation();
      this.onSuccess();
      return result;
    } catch (error) {
      this.onFailure();
      throw error;
    }
  }

  /**
   * Gets the current circuit state
   */
  getState(): CircuitState {
    return this.state;
  }

  /**
   * Gets circuit breaker status information
   */
  getStatus(): {
    state: CircuitState;
    failureCount: number;
    lastFailureTime: number;
    nextAttemptTime: number;
    isHealthy: boolean;
  } {
    return {
      state: this.state,
      failureCount: this.failureCount,
      lastFailureTime: this.lastFailureTime,
      nextAttemptTime: this.nextAttemptTime,
      isHealthy: this.state === CircuitState.CLOSED
    };
  }

  /**
   * Manually resets the circuit breaker
   */
  reset(): void {
    this.state = CircuitState.CLOSED;
    this.failureCount = 0;
    this.lastFailureTime = 0;
    this.nextAttemptTime = 0;
    this.halfOpenCalls = 0;
  }

  /**
   * Checks if the circuit breaker allows requests
   */
  canExecute(): boolean {
    if (this.state === CircuitState.CLOSED) {
      return true;
    }

    if (this.state === CircuitState.OPEN) {
      return Date.now() >= this.nextAttemptTime;
    }

    if (this.state === CircuitState.HALF_OPEN) {
      return this.halfOpenCalls < this.config.halfOpenMaxCalls;
    }

    return false;
  }

  private onSuccess(): void {
    const wasOpen = this.state === CircuitState.HALF_OPEN;

    this.failureCount = 0;
    this.state = CircuitState.CLOSED;
    this.halfOpenCalls = 0;

    // Log recovery to Sentry
    if (wasOpen) {
      Sentry.addBreadcrumb({
        category: 'circuit-breaker',
        message: 'Circuit breaker recovered to CLOSED state',
        level: 'info',
        data: {
          previousState: 'HALF_OPEN',
          newState: 'CLOSED'
        }
      });
    }
  }

  private onFailure(): void {
    this.failureCount++;
    this.lastFailureTime = Date.now();

    // Log failure breadcrumb
    Sentry.addBreadcrumb({
      category: 'circuit-breaker',
      message: 'Circuit breaker failure recorded',
      level: 'warning',
      data: {
        failureCount: this.failureCount,
        threshold: this.config.failureThreshold,
        state: this.state
      }
    });

    // Check if we should open the circuit
    if (this.failureCount >= this.config.failureThreshold) {
      const previousState = this.state;
      this.state = CircuitState.OPEN;
      this.nextAttemptTime = Date.now() + this.config.recoveryTimeout;

      // CRITICAL ALERT: Circuit breaker opened
      Sentry.captureMessage('Circuit breaker opened - service degraded', {
        level: 'error',
        tags: {
          component: 'circuit-breaker',
          state: 'OPEN',
          previousState
        },
        extra: {
          failureCount: this.failureCount,
          threshold: this.config.failureThreshold,
          recoveryTimeoutMs: this.config.recoveryTimeout,
          nextAttemptIn: this.config.recoveryTimeout + 'ms'
        }
      });
    }
  }
}

// Create singleton instances for different services
export const aiAnalysisCircuitBreaker = new CircuitBreaker({
  failureThreshold: 3,      // Open after 3 failures
  recoveryTimeout: 30000,   // Wait 30 seconds before trying again
  monitoringPeriod: 60000,  // Count failures over 1 minute
  halfOpenMaxCalls: 2       // Allow 2 test calls in half-open state
});

export const imageProcessingCircuitBreaker = new CircuitBreaker({
  failureThreshold: 5,      // Open after 5 failures
  recoveryTimeout: 15000,   // Wait 15 seconds before trying again
  monitoringPeriod: 30000,  // Count failures over 30 seconds
  halfOpenMaxCalls: 3       // Allow 3 test calls in half-open state
});
